import React, { useState, useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "@/components/ui/button";
import { Pencil, Save, X, Undo2, Redo2, Bold, Italic, Heading2 } from "lucide-react";

function _escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Convertit les marqueurs markdown-like (**gras**, *italique*) d'une ligne déjà
 * échappée en balises HTML.
 */
function _applyInlineMarkdown(escapedText) {
  return escapedText
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

/**
 * Convert plain-text autobiography into HTML for TipTap.
 *
 * Rules:
 * - Lines matching ═{10,} become <hr />
 * - ALL-UPPERCASE lines (length > 3, containing letters) become <h2>
 * - Consecutive non-empty lines are grouped into a single <p>
 * - Empty lines flush the current paragraph
 */
export function parseAutobiographyToHTML(text) {
  if (!text) return "<p></p>";

  const lines = text.split("\n");
  let html = "";
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      html += `<p>${paragraphBuffer.join(" ")}</p>`;
      paragraphBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (/═{10,}/.test(trimmed)) {
      flushParagraph();
      html += "<hr />";
    } else if (trimmed.startsWith("## ")) {
      flushParagraph();
      const content = _applyInlineMarkdown(_escapeHtml(trimmed.slice(3).trim()));
      html += `<h2>${content}</h2>`;
    } else if (
      trimmed.length > 3 &&
      /[A-ZÀ-Ü]/.test(trimmed) &&
      trimmed === trimmed.toUpperCase() &&
      /[A-ZÀ-Ü]/.test(trimmed.replace(/[^A-ZÀ-Üa-zà-ü]/g, ""))
    ) {
      flushParagraph();
      html += `<h2>${_applyInlineMarkdown(_escapeHtml(trimmed))}</h2>`;
    } else if (trimmed === "") {
      flushParagraph();
    } else {
      paragraphBuffer.push(_applyInlineMarkdown(_escapeHtml(trimmed)));
    }
  }

  flushParagraph();
  return html || "<p></p>";
}

/**
 * Convert TipTap editor JSON back to plain text.
 */
export function serializeHTMLToText(editor) {
  if (!editor) return "";

  const json = editor.getJSON();
  if (!json.content) return "";

  const parts = [];

  for (const node of json.content) {
    if (node.type === "horizontalRule") {
      parts.push(
        "\n═══════════════════════════════════════════════════════════════\n"
      );
    } else if (node.type === "heading") {
      const text = extractText(node);
      parts.push(`## ${text}\n`);
    } else if (node.type === "paragraph") {
      const text = extractText(node);
      parts.push(text + "\n");
    }
  }

  return parts.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Extract plain text from a TipTap node, encoding bold/italic marks as
 * markdown-style markers (**bold**, *italic*) so they survive the round-trip
 * through plain-text storage.
 */
export function extractText(node) {
  if (!node.content) return "";
  return node.content
    .map((child) => {
      const text = child.text || "";
      if (!text) return "";
      const marks = (child.marks || []).map((m) => m.type);
      const isBold = marks.includes("bold");
      const isItalic = marks.includes("italic");
      if (isBold && isItalic) return `***${text}***`;
      if (isBold) return `**${text}**`;
      if (isItalic) return `*${text}*`;
      return text;
    })
    .join("");
}

const AutobiographyEditor = ({ autobiography, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const savedHTMLRef = useRef("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2] },
      }),
      Placeholder.configure({
        placeholder: "Votre autobiographie apparaitra ici...",
      }),
    ],
    content: parseAutobiographyToHTML(autobiography),
    editable: false,
  });

  // Sync content when autobiography prop changes (but not while editing)
  useEffect(() => {
    if (editor && !isEditing) {
      editor.commands.setContent(parseAutobiographyToHTML(autobiography));
    }
  }, [autobiography, editor, isEditing]);

  const handleEdit = useCallback(() => {
    if (!editor) return;
    savedHTMLRef.current = editor.getHTML();
    editor.setEditable(true);
    setIsEditing(true);
  }, [editor]);

  const handleCancel = useCallback(() => {
    if (!editor) return;
    editor.commands.setContent(savedHTMLRef.current);
    editor.setEditable(false);
    setIsEditing(false);
  }, [editor]);

  const handleSave = useCallback(() => {
    if (!editor) return;
    const plainText = serializeHTMLToText(editor);
    editor.setEditable(false);
    setIsEditing(false);
    if (onSave) {
      onSave(plainText);
    }
  }, [editor, onSave]);

  if (!editor) return null;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {isEditing ? (
          <>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={
                  editor.isActive("bold") ? "bg-muted" : ""
                }
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className={
                  editor.isActive("italic") ? "bg-muted" : ""
                }
              >
                <Italic className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={
                  editor.isActive("heading", { level: 2 }) ? "bg-muted" : ""
                }
              >
                <Heading2 className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
              >
                <Redo2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="gap-1"
              >
                <X className="w-4 h-4" />
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="gap-1"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </Button>
            </div>
          </>
        ) : (
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="gap-1"
            >
              <Pencil className="w-4 h-4" />
              Modifier
            </Button>
          </div>
        )}
      </div>

      {/* Editor */}
      <div
        className={`rounded-lg transition-all ${
          isEditing
            ? "ring-2 ring-primary ring-offset-2"
            : ""
        }`}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default AutobiographyEditor;
